import { dia } from '@clientio/rappid';
import { TranslocoService } from '@jsverse/transloco';
import { SelectionType } from 'guideline/domain';
import { of, switchMap } from 'rxjs';
import { ComponentConverter } from 'shared/domain';

import {
  ActivityDefinitionDto,
  ComponentDto,
  FlowchartElementType,
  FlowchartLinkDto,
  FlowchartNodeDto,
  FlowchartSubflowDto,
  ProposedComponentDto,
} from '@treetop/shared/model';

import { BasicNode } from './BasicNode';
import { FlowchartNode } from './FlowchartNode';
import { IFlowchartNode } from './IFlowchartNode';
import { FlowchartSidebarStoreService } from '../../../../../guideline/feature-sidebar/src/lib/infrastructure/flowchart-sidebar-store.service';
import OperationsService from '../infrastructure/operations/operations.service';
import SubflowService from '../infrastructure/operations/subflow.service';

export class FlowchartSubflowNode extends BasicNode<FlowchartSubflowDto> implements IFlowchartNode {
  constructor(element: FlowchartSubflowDto) {
    super(element);
  }

  addOutgoingLink(link: FlowchartLinkDto): void {
    this.element.outgoingLinks = this.element.outgoingLinks || [];
    this.element.outgoingLinks.push(link);
  }

  removeOutgoingLink(linkIndex: number): void {
    this.element.outgoingLinks.splice(linkIndex, 1);
  }

  addToGraph(
    graph: dia.Graph,
    paper: dia.Paper,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService: TranslocoService,
    allowEvents = false,
  ): void {
    this.addSubflow(paper, graph, operationsService, subflowService, translocoService, allowEvents);
  }

  setPropertiesForInit(element: dia.Cell): void {
    super.setPropertiesForInit(element);
    this.element.elementType = FlowchartElementType.FlowchartSubflow;
    if (element.attributes.isContainerSubComponent) {
      this.element.parentId = element.attributes.parentContainerId;
    }
  }

  async setPropertiesAfterSearch(
    definition: ComponentDto | ProposedComponentDto,
    medicationRequests: ActivityDefinitionDto[],
    componentConverter: ComponentConverter,
    selectedElementView?: dia.ElementView,
    translocoService?: TranslocoService,
    flowchartSidebarStoreService?: FlowchartSidebarStoreService,
  ): Promise<void> {
    const copiedObject: FlowchartSubflowDto = JSON.parse(JSON.stringify(this.element));

    if (selectedElementView) {
      copiedObject.width = selectedElementView.model.attributes.size.width;
      copiedObject.height = selectedElementView.model.attributes.size.height;
    }

    of(definition)
      .pipe(
        switchMap((definition) => {
          if (definition.code.startsWith('PC-')) {
            return of(componentConverter.convertProposedComponentToAction(definition));
          } else {
            return componentConverter.convertActivityToAction(definition, medicationRequests, this.element.uniqueId);
          }
        }),
      )
      .subscribe((action) => {
        copiedObject.guidelineAction = action;
        copiedObject.guidelineActionId = copiedObject.guidelineAction.id;

        flowchartSidebarStoreService.setDto({
          element: copiedObject,
          type: SelectionType.Flowchart,
        });
      });
  }

  private addSubflow(
    paper: dia.Paper,
    graph: dia.Graph,
    operationsService: OperationsService,
    subflowService: SubflowService,
    translocoService: TranslocoService,
    allowEvents = false,
  ) {
    const subflow = subflowService.createSubflow(this.element.positionX, this.element.positionY, this.element.id, this.element.uniqueId);
    graph.addCell(subflow);
    const title = this.getTitle(this.element, translocoService);
    const description = this.getDescription(this.element);
    //add subflow header
    const subflowHeader = subflowService.createSubflowHeader(title, subflow.attributes.position.x, subflow.attributes.position.y);
    graph.addCell(subflowHeader);
    subflow.embed(subflowHeader);
    operationsService.addTooltip(subflowHeader.id as string);
    operationsService.setClickableForSmartbook(this.element.id, description);
    operationsService.setClickableForSmartbook(subflowHeader.id, description);

    this.element.children?.forEach((el) => {
      const subflowElement = this.addCommonSubElement(paper, graph, operationsService, subflowService, el, translocoService, allowEvents);

      if (subflowElement) {
        subflowService.addElementToSubflow(this.element.id, subflowElement);
        graph.addCell(subflowElement);
      }
    });
    operationsService.addElementTags(this.element);
  }

  private addCommonSubElement(
    paper: dia.Paper,
    graph: dia.Graph,
    operationsService: OperationsService,
    subflowService: SubflowService,
    el: FlowchartNodeDto,
    translocoService: TranslocoService,
    allowEvents = false,
  ): dia.Cell {
    const subNode = new FlowchartNode(el);
    subNode.addToGraph(graph, paper, operationsService, subflowService, translocoService, allowEvents);
    return graph.getCell(subNode.getElement().id);
  }

  private getDescription(element: FlowchartSubflowDto): string {
    return element.guidelineAction?.description;
  }

  private getTitle(element: FlowchartSubflowDto, translocoService: TranslocoService): string {
    if (element.guidelineAction?.title) {
      return element.guidelineAction?.title;
    } else {
      return translocoService.translate('SetTitle');
    }
  }
}
